/**
 * CustomerJobMaterialsSummary — calm, read-only per-job materials snapshot
 * for the Customer Profile.
 *
 * • System sold: derived from the bound JobSpec label (no parsing of names).
 * • Top SKUs:    first N items from the bound spec BOM (most BOMs already lead
 *                with the highest-value canonical pieces). Pure presentation.
 * • Link:        deep link to the full Job Detail BOM view.
 *
 * No status pills. No alignment indicators. No vendor or pricing.
 */

import { Link } from "react-router-dom";
import { Package, ExternalLink } from "lucide-react";
import { resolveBoundSpec, formatSpecRef } from "@/data/jobSpec";
import type { Job } from "@/data/types";

const isUuidLike = (value: string) =>
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value.trim());

const getJobDisplayId = (job: Job) => {
  const jobNumber = String((job as { jobNumber?: string }).jobNumber ?? "").trim();
  if (jobNumber) return jobNumber;
  return isUuidLike(job.id) ? "JOB" : job.id;
};

interface CustomerJobMaterialsSummaryProps {
  job: Job;
  topN?: number;
}

export function CustomerJobMaterialsSummary({ job, topN = 5 }: CustomerJobMaterialsSummaryProps) {
  const spec = resolveBoundSpec(job.boundJobSpec);

  if (!spec || spec.billOfMaterials.length === 0) {
    return (
      <div className="rounded-lg border bg-muted/10 p-3">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <Link to={`/jobs/${job.id}`} className="font-mono text-[10px] text-primary hover:underline font-semibold">
              {getJobDisplayId(job)}
            </Link>
            <span className="text-xs font-medium">{job.projectType}</span>
          </div>
          <span className="text-[10px] text-muted-foreground">No spec bound</span>
        </div>
      </div>
    );
  }

  const top = spec.billOfMaterials.slice(0, topN);
  const remaining = spec.billOfMaterials.length - top.length;

  return (
    <div className="rounded-lg border p-3 space-y-2">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <Link to={`/jobs/${job.id}`} className="font-mono text-[10px] text-primary hover:underline font-semibold">
            {getJobDisplayId(job)}
          </Link>
          <span className="text-xs font-medium">{spec.label}</span>
        </div>
        <span className="font-mono text-[10px] text-muted-foreground">
          {formatSpecRef({ specId: spec.specId, versionNumber: spec.versionNumber })}
        </span>
      </div>

      <ul className="space-y-1">
        {top.map((b) => (
          <li key={b.sku} className="flex items-center justify-between text-[11px]">
            <span className="truncate text-foreground">{b.name}</span>
            <span className="text-muted-foreground tabular-nums shrink-0 ml-2">
              {b.quantity}{b.unit ? ` ${b.unit}` : ""}
            </span>
          </li>
        ))}
      </ul>

      <div className="flex items-center justify-between pt-1 border-t">
        <span className="text-[10px] text-muted-foreground">
          {remaining > 0 ? `+${remaining} more item${remaining === 1 ? "" : "s"}` : `${spec.billOfMaterials.length} item${spec.billOfMaterials.length === 1 ? "" : "s"} total`}
        </span>
        <Link
          to={`/jobs/${job.id}`}
          className="text-[10px] text-primary hover:underline inline-flex items-center gap-1"
        >
          View full BOM <ExternalLink className="h-2.5 w-2.5" />
        </Link>
      </div>
    </div>
  );
}

interface CustomerMaterialsSectionProps {
  jobs: Job[];
}

export function CustomerMaterialsSection({ jobs }: CustomerMaterialsSectionProps) {
  if (jobs.length === 0) return null;
  // Show only jobs that have a bound spec OR show empty-spec rows for completeness?
  // Per spec: per-job summary. Render all jobs; the row gracefully handles "no spec bound".
  return (
    <div className="space-y-2">
      <h2 className="text-xs font-bold text-muted-foreground uppercase tracking-[0.12em] flex items-center gap-2">
        <Package className="h-4 w-4" /> Project Materials
      </h2>
      <div className="space-y-2">
        {jobs.map((j) => (
          <CustomerJobMaterialsSummary key={j.id} job={j} />
        ))}
      </div>
    </div>
  );
}
